import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStats } from '../redux/actions/statsActions';
import { fetchSalesOverview } from '../redux/actions/salesActions';
import { FiShoppingCart, FiUsers, FiTrendingUp, FiDollarSign, FiPackage, FiAlertCircle } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const Dashboard = () => {
    const dispatch = useDispatch();
    const { loading: statsLoading, stats, error: statsError } = useSelector((state) => state.stats);
    const { loading: salesLoading, salesData, error: salesError } = useSelector((state) => state.sales);
    const [period, setPeriod] = useState("monthly"); // Default: Monthly

    useEffect(() => {
        dispatch(fetchStats());
        dispatch(fetchSalesOverview(period));
    }, [dispatch, period]);

    return (
        <div className="min-h-screen bg-gray-50 p-8 font-sans">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard Overview</h1>

                {statsLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {[1, 2, 3, 4].map((_, index) => (
                            <Skeleton key={index} height={120} borderRadius={12} />
                        ))}
                    </div>
                ) : statsError ? (
                    <div className="bg-red-50 p-4 rounded-lg text-red-600 flex items-center">
                        <FiAlertCircle className="mr-2" />
                        {statsError}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <StatCard
                            icon={<FiShoppingCart className="text-2xl text-teal-600" />}
                            label="Total Orders"
                            value={stats.totalOrders}
                            growth={stats.orderGrowth}
                        />
                        <StatCard
                            icon={<FiUsers className="text-2xl text-purple-600" />}
                            label="Total Customers"
                            value={stats.totalCustomers}
                            growth={stats.customerGrowth}
                        />
                        <StatCard
                            icon={<FiPackage className="text-2xl text-blue-600" />}
                            label="Total Products"
                            value={stats.totalProducts}
                            growth={stats.productGrowth}
                        />
                        <StatCard
                            icon={<FiDollarSign className="text-2xl text-green-600" />}
                            label="Total Revenue"
                            value={`₹${stats.totalRevenue?.toLocaleString()}`}
                            growth={stats.revenueGrowth}
                        />
                    </div>
                )}

                {/* Sales Overview Chart with Period Selection */}
                <SalesOverview 
                    salesData={salesData} 
                    loading={salesLoading} 
                    error={salesError} 
                    period={period} 
                    setPeriod={setPeriod} 
                />
            </div>
        </div>
    );
};

// Reusable Statistic Card Component
const StatCard = ({ icon, label, value, growth }) => (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
        <div className="flex items-center justify-between">
            <div className="bg-gray-100 p-4 rounded-lg">{icon}</div>
            <FiTrendingUp className="text-xl text-green-500" />
        </div>
        <h3 className="text-gray-500 text-sm mt-4">{label}</h3>
        <div className="flex items-baseline mt-2">
            <p className="text-2xl font-bold text-gray-800">{value}</p>
            <span className="text-sm text-green-500 ml-2">+{growth}</span>
        </div>
    </div>
);

// Sales Overview Component with Period Selection
const SalesOverview = ({ salesData, loading, error, period, setPeriod }) => {
    return (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">Sales Overview</h2>
                
                {/* Dropdown for selecting period */}
                <select 
                    className="border border-gray-300 rounded px-3 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                </select>
            </div>

            {loading ? (
                <Skeleton height={400} borderRadius={12} />
            ) : error ? (
                <div className="bg-red-50 p-4 rounded-lg text-red-600 flex items-center">
                    <FiAlertCircle className="mr-2" />
                    {error}
                </div>
            ) : (
                <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={salesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis dataKey="period" stroke="#666" />
                            <YAxis stroke="#666" />
                            <Tooltip 
                                contentStyle={{ 
                                    backgroundColor: '#fff', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '8px', 
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' 
                                }} 
                            />
                            <Legend />
                            <Line 
                                type="monotone" 
                                dataKey="totalRevenue" 
                                stroke="#8884d8" 
                                strokeWidth={2} 
                                activeDot={{ r: 8 }} 
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default Dashboard;